import { useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { FieldArray, useField } from "formik"
import { Box, Stack, Typography } from "@mui/material"

import { CreateUpdateDeliveryFormValues, DeliveryTimeValues } from "../../CreateUpdateDeliveryTimePopover"
import { LocalizedStrict } from "../../../../../../../../modules/localization/components/AppLocalized"
import { generateDisabledTimeSlots } from "../../helpers"
import { StandardButton } from "../../../../../../buttons/StandardButton"
import { useDeliveryTimeLimits } from "./useDeliveryTimeLimits"
import { DeliveryTimeDay } from "../../../../form/formTypes"
import { DeliveryTimeWarning } from "./DeliveryTimeWarning"
import { DeliveryTimeRow } from "./DeliveryTimeRow"

const AUTO_OPEN_DELAY_MS = 200
const FORCE_FLAG_RESET_MS = 300

const useStyles = makeStyles()((theme) => ({
    subtitle: {
        color: theme.palette.tailwind.slate[700],
    },
}))

type DeliveryTimesFieldArrayProps = {
    openingHours: DeliveryTimeDay[]
    showTimeWarningMessage: boolean
    maxAllowedDeliveries: number
}

export function DeliveryTimesFieldArray({
    openingHours,
    showTimeWarningMessage,
    maxAllowedDeliveries,
}: DeliveryTimesFieldArrayProps) {
    const { classes } = useStyles()
    const [{ value: deliveryDate }] = useField<CreateUpdateDeliveryFormValues["deliveryDate"]>("deliveryDate")
    const [{ value: deliveryTimes }] = useField<DeliveryTimeValues[]>("deliveryTimes")
    const [forceOpenIndex, setForceOpenIndex] = useState<number | null>(null)
    const { isToday, minMaxTime } = useDeliveryTimeLimits(deliveryDate, openingHours)

    const lastAutoOpenedDateRef = useRef<string | null>(null)
    const dateKey =
        deliveryDate && typeof (deliveryDate as any).toISODate === "function"
            ? (deliveryDate as any).toISODate()
            : deliveryDate
            ? String(deliveryDate)
            : null

    useEffect(() => {
        if (!deliveryDate) return

        const firstEmpty = !deliveryTimes?.length || !deliveryTimes[0]?.time
        const notOpenedForThisDate = dateKey && lastAutoOpenedDateRef.current !== dateKey

        if (firstEmpty && notOpenedForThisDate && forceOpenIndex === null) {
            const t = setTimeout(() => {
                setForceOpenIndex(0)
                lastAutoOpenedDateRef.current = dateKey!
            }, AUTO_OPEN_DELAY_MS)
            return () => clearTimeout(t)
        }

        return
    }, [dateKey, deliveryTimes?.[0]?.time, forceOpenIndex, deliveryDate])

    useEffect(() => {
        if (forceOpenIndex !== null) {
            const timeout = setTimeout(() => setForceOpenIndex(null), FORCE_FLAG_RESET_MS)
            return () => clearTimeout(timeout)
        }
        return
    }, [forceOpenIndex])

    return (
        <Stack gap=".75rem">
            {showTimeWarningMessage ? (
                <DeliveryTimeWarning isToday={!!isToday} minMaxTime={minMaxTime} />
            ) : (
                <>
                    <LocalizedStrict id="create-order-delivery-times-dialog-delivery-times">
                        <Typography variant="body1" className={classes.subtitle}>
                            Delivery times
                        </Typography>
                    </LocalizedStrict>
                    <FieldArray
                        name="deliveryTimes"
                        render={(arrayHelpers) => {
                            const items: DeliveryTimeValues[] = arrayHelpers.form.values.deliveryTimes

                            return (
                                <Stack gap="1rem">
                                    {items.map((item, index: number) => {
                                        const otherTimes = items
                                            .map((el, i) => (i !== index ? el.time?.toFormat("HH:mm") : null))
                                            .filter((t): t is string => typeof t === "string")
                                        const shouldDisableTime = generateDisabledTimeSlots(otherTimes)

                                        return (
                                            <DeliveryTimeRow
                                                key={`delivery-${index}`}
                                                index={index}
                                                total={items.length}
                                                shouldDisableTime={shouldDisableTime}
                                                shouldForceOpen={forceOpenIndex === index}
                                                onRemove={() => arrayHelpers.remove(index)}
                                                minMaxTime={minMaxTime}
                                            />
                                        )
                                    })}
                                    {items.length < maxAllowedDeliveries && (
                                        <Box>
                                            <LocalizedStrict id="create-order-delivery-times-dialog-add-delivery-button">
                                                <StandardButton
                                                    type="button"
                                                    color="blue"
                                                    variant="text"
                                                    size="small"
                                                    fullWidth={false}
                                                    onClick={() => {
                                                        const newIndex = items.length
                                                        arrayHelpers.push({ time: null, orderLines: [] })
                                                        setForceOpenIndex(newIndex)
                                                    }}
                                                >
                                                    + Add delivery
                                                </StandardButton>
                                            </LocalizedStrict>
                                        </Box>
                                    )}
                                </Stack>
                            )
                        }}
                    />
                </>
            )}
        </Stack>
    )
}
